import { Injectable } from '@angular/core';
import { GridOptions } from 'ag-grid';
import { ColumnState } from 'ag-grid/dist/lib/columnController/columnController';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { ICurrentModuleActions } from 'app/core/interfaces/rules-by-code.interface';
import { PublicGridLayout } from './grid-layout-menu.interface';

export interface GridLayoutContext {
  grid: GridOptions;
  moduleActionId: number | null;
  title: string;
  buttonClass?: string;
  readConfig?: () => string;
  applyConfig?: (config: string) => void;
}

export function moduleActionIdOf(module: ICurrentModuleActions | null, actionKey: string): number | null {
  const action = module && module.actions ? module.actions[actionKey] : null;
  return action ? action.moduleactionid : null;
}

@Injectable()
export class GridLayoutStore {
  private contextSubject = new BehaviorSubject<GridLayoutContext | null>(null);
  private manageSubject = new Subject<void>();
  private applySubject = new Subject<PublicGridLayout>();
  private unpublishSubject = new Subject<PublicGridLayout>();
  private changedSubject = new Subject<void>();

  context$: Observable<GridLayoutContext | null> = this.contextSubject.asObservable();
  manage$: Observable<void> = this.manageSubject.asObservable();
  applyRequest$: Observable<PublicGridLayout> = this.applySubject.asObservable();
  unpublishRequest$: Observable<PublicGridLayout> = this.unpublishSubject.asObservable();
  changed$: Observable<void> = this.changedSubject.asObservable();

  get context(): GridLayoutContext | null {
    return this.contextSubject.getValue();
  }

  set(context: GridLayoutContext) {
    this.contextSubject.next(context);
  }

  follow(trigger$: Observable<{}>, build: () => GridLayoutContext): Subscription {
    return trigger$.subscribe({ next: () => this.set(build()) });
  }

  openManage() {
    this.manageSubject.next();
  }

  requestApply(item: PublicGridLayout) {
    this.applySubject.next(item);
  }

  requestUnpublish(item: PublicGridLayout) {
    this.unpublishSubject.next(item);
  }

  notifyChanged() {
    this.changedSubject.next();
  }

  readConfig(): string | null {
    const context = this.context;
    if (context && context.readConfig) {
      return context.readConfig();
    }
    if (!context || !context.grid || !context.grid.columnApi) {
      return null;
    }
    return JSON.stringify(context.grid.columnApi.getColumnState());
  }

  applyConfig(config: string | null): boolean {
    const context = this.context;
    if (!context || !config) {
      return false;
    }
    if (context.applyConfig) {
      context.applyConfig(config);
      return true;
    }
    const state = this.parseColumnState(config);
    if (!state || !context.grid || !context.grid.columnApi) {
      return false;
    }
    context.grid.columnApi.setColumnState(state);
    return true;
  }

  private parseColumnState(config: string): ColumnState[] | null {
    let state: ColumnState[];
    try {
      state = JSON.parse(config);
    } catch (e) {
      return null;
    }
    const valid = Array.isArray(state) && state.every((col: ColumnState) => !!col && typeof col.colId === 'string' && !!col.colId);
    return valid ? state : null;
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/grid-layout-menu/grid-layout-store.ts